import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PropRow } from '../activity-drawer/ui/PropRow';

export interface OptionItem {
  value: string;
  label: string;
  color?: string | null;
  description?: string | null;
}

interface InlineOptionRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  options: OptionItem[];
  /** Selezione immediata; null = voce di rimozione. L'optimistic/revert è del chiamante. */
  onSelect: (value: string | null) => void;
  canEdit: boolean;
  placeholder?: string;
  /** Voce per azzerare la selezione (es. "Non specificato"); assente = non rimovibile. */
  clearLabel?: string;
  /** 'chip' = valore come chip col colore dell'opzione; 'dot' = pallino + testo. */
  valueStyle?: 'chip' | 'dot' | 'text';
  loading?: boolean;
  searchPlaceholder?: string;
}

/**
 * Riga di proprietà con scelta da elenco a salvataggio immediato: il valore è
 * un chip/testo, il click apre un dropdown ricercabile (stesso pattern di
 * UserSelectRow del drawer "Nuova anagrafica").
 */
export function InlineOptionRow({
  icon, label, value, options, onSelect, canEdit, placeholder = 'Vuoto',
  clearLabel, valueStyle = 'text', loading, searchPlaceholder = 'Cerca...',
}: InlineOptionRowProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) || null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = options.filter((o) =>
    (o.label + ' ' + (o.description || '')).toLowerCase().includes(query.toLowerCase()),
  );

  const pick = (v: string | null) => {
    setOpen(false);
    if (v !== (value || null)) onSelect(v);
  };

  return (
    <div ref={boxRef} className="relative">
      <PropRow
        icon={icon}
        label={label}
        onClick={canEdit ? () => { setOpen((o) => !o); setQuery(''); } : undefined}
        trailing={loading ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : undefined}
      >
        {selected ? (
          valueStyle === 'chip' ? (
            <span
              className="inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-[11.5px] font-medium"
              style={{
                backgroundColor: selected.color ? `${selected.color}20` : 'rgba(55,53,47,0.08)',
                color: selected.color || undefined,
              }}
            >
              <span className="truncate">{selected.label}</span>
            </span>
          ) : valueStyle === 'dot' ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ backgroundColor: selected.color || '#6B7280' }}
              />
              <span className="truncate">{selected.label}</span>
            </span>
          ) : (
            <span className="truncate">{selected.label}</span>
          )
        ) : (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">{placeholder}</span>
        )}
      </PropRow>
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-0.5 overflow-hidden rounded-md border border-[rgba(55,53,47,0.16)] bg-white shadow-lg dark:border-white/15 dark:bg-[#252525]">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
            placeholder={searchPlaceholder}
            className="w-full border-b border-[rgba(55,53,47,0.09)] bg-transparent px-3 py-1.5 text-[13px] text-[#37352F] focus:outline-none dark:border-white/10 dark:text-white"
          />
          <div className="max-h-48 overflow-y-auto py-1">
            {clearLabel && value && (
              <button
                type="button"
                onClick={() => pick(null)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-[rgba(55,53,47,0.5)] hover:bg-[rgba(55,53,47,0.06)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
              >
                {clearLabel}
              </button>
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] ${
                  o.value === value ? 'bg-[rgba(55,53,47,0.04)] font-medium' : ''
                } text-[#37352F] dark:text-white`}
              >
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: o.color || '#6B7280' }}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{o.label}</span>
                  {o.description && (
                    <span className="truncate text-[11px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">{o.description}</span>
                  )}
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">Nessun risultato</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
